import { percentage } from '@/hooks/useGetPosition';
import { TradeData } from '@/types/trade';
import { formatDisplay } from '@/utils/format-display';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Sheet } from 'react-modal-sheet';
import FeesCard from './FeesCard';
import { useAddress } from '@/store/selector';
import { useGetFees } from './useGetFees';
import { useMemo } from 'react';

import BigNumber from 'bignumber.js';
import dayjs from 'dayjs';
import { CloseIcon } from '@/icons';
type Props = {
    item: TradeData;
    isOpen: boolean;
    onClose: () => void;
};

export default function SheetDetail({ item, isOpen, onClose }: Props) {
    const { t } = useTranslation();

    const address = useAddress();

    const { data } = useGetFees(
        address,
        item.indexPrice,
        item.index,
        item.createdAt,
        item.buy,
        item.initialPosToken,
        item.leverage
    );

    const earn = useMemo(() => {
        const fundingFee = new BigNumber(data?.fundingFee || 0);
        const rolloverFee = new BigNumber(data?.rolloverFee || 0);
        const netEarn = new BigNumber(item.net_pnl?.earn || 0);

        return netEarn.minus(fundingFee).minus(rolloverFee).toNumber();
    }, [data?.fundingFee, data?.rolloverFee, item.net_pnl?.earn]);

    const pnl = useMemo(() => {
        const collateral = new BigNumber(item.collateralValue || 0);
        if (collateral.isZero()) return 0; // tránh chia cho 0

        const value = new BigNumber(earn).div(collateral).multipliedBy(100);

        console.debug('collateral-------', value.toString());

        // Giới hạn nhỏ nhất là -100
        const minValue = new BigNumber(-100);
        const finalValue = BigNumber.maximum(value, minValue);

        return finalValue.toNumber();
    }, [earn, item.collateralValue]);

    return (
        <Sheet isOpen={isOpen} onClose={onClose} detent="content">
            <Sheet.Container className="flex justify-end">
                <Sheet.Header />
                <Sheet.Content className="relative">
                    <button
                        onClick={onClose}
                        className="cursor-pointer absolute -top-7 right-3 z-20 p-1.5 text-slate-400  transition-colors rounded-full "
                    >
                        <CloseIcon />
                    </button>
                    <div className="overflow-y-auto flex-1 px-6 pt-1 pb-4 relative">
                        <div className="flex items-center gap-3">
                            <div className="relative w-10 h-10 rounded-full bg-gray-200 overflow-hidden flex items-center justify-center border border-[#E6E8EC] shrink-0">
                                <img
                                    className="w-full h-full object-cover"
                                    src={item.imgUrl}
                                    alt={item.pairName}
                                />
                            </div>
                            <div>
                                <div className="flex items-center gap-2 mb-0.5">
                                    <div className="font-bold text-base tracking-tight leading-tight">
                                        {item.pairName}
                                    </div>
                                    <div className="flex gap-1">
                                        <span
                                            className={clsx(
                                                'text-[10px] font-bold border px-1 py-px rounded  leading-none uppercase',
                                                {
                                                    'text-[#00C076] border-[#00C076]/30 bg-[#00C076]/10':
                                                        item.type == 'Long',
                                                    'text-[#FF4D4F] border-[#FF4D4F]/30 bg-[#FF4D4F]/10':
                                                        item.type !== 'Long',
                                                }
                                            )}
                                        >
                                            {item.type}
                                        </span>
                                        <span className="text-[10px] font-bold text-[#6F767E] border border-[#E6E8EC]  px-1 py-px rounded bg-gray-100 leading-none">
                                            {item.leverage}x
                                        </span>
                                    </div>
                                </div>
                                <div className="text-[10px] text-[#6F767E] font-medium">
                                    {t('Open At')}{' '}
                                    {dayjs
                                        .utc(Number(item.createdAt) * 1000)
                                        .format('DD/MM/YYYY - HH:mm:ss')}{' '}
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 gap-y-1.5 mb-5 mt-4">
                            <div className="flex justify-between items-center py-1.5">
                                <span className="text-xs text-[#6F767E] ">
                                    {t('Use')}
                                </span>
                                <span className="text-xs font-semibold text-slate-900 ">
                                    {formatDisplay(item.collateralValue, {
                                        decimalToShow: 2,
                                    })}{' '}
                                    {item.tradeCoinName}
                                </span>
                            </div>
                            <div className="flex justify-between items-center py-1.5">
                                <span className="text-xs text-[#6F767E] ">
                                    {t('Position')}
                                </span>
                                <span className="text-xs font-semibold text-slate-900 ">
                                    {formatDisplay(item.sizeValue, {
                                        decimalToShow: 2,
                                    })}{' '}
                                    {item.tradeCoinName}
                                </span>
                            </div>
                            <div className="flex justify-between items-center py-1.5">
                                <span className="text-xs text-[#6F767E] ">
                                    {t('To Earn')}
                                </span>
                                <span className="text-xs font-semibold text-[#00C076]">
                                    +
                                    {formatDisplay(item.earnBuyTp, {
                                        decimalToShow: 2,
                                    })}{' '}
                                    {item.tradeCoinName}
                                </span>
                            </div>
                            <div className="flex justify-between items-center py-1.5">
                                <span className="text-xs text-[#6F767E] ">
                                    {t('Entry Price')}
                                </span>
                                <span className="text-xs font-semibold text-slate-900 ">
                                    {formatDisplay(Number(item.open_price), {
                                        decimalToShow: item.decimalToShow,
                                    })}
                                </span>
                            </div>
                            <div className="flex justify-between items-center py-1.5">
                                <span className="text-xs text-[#6F767E] ">
                                    {t('Current Price')}
                                </span>
                                <span className="text-xs font-semibold text-slate-900 ">
                                    {formatDisplay(Number(item.price), {
                                        decimalToShow: item.decimalToShow,
                                    })}
                                </span>
                            </div>
                            <div className="flex justify-between items-center py-1.5">
                                <span className="text-xs text-[#6F767E] ">
                                    {t('Liq. Price')}
                                </span>
                                <span className="text-xs font-semibold text-orange-500">
                                    {formatDisplay(
                                        Number(item.liquidationPrice),
                                        {
                                            decimalToShow: item.decimalToShow,
                                        }
                                    )}
                                </span>
                            </div>
                        </div>
                        <FeesCard item={item} />
                        <div
                            className={clsx(
                                'border border-dashed rounded-xl py-4 flex flex-col items-center justify-center relative overflow-hidden mb-4',
                                {
                                    'border-[#10b981]/30 bg-[#10b981]/5':
                                        earn >= 0,
                                    'border-[#ef4444]/30 bg-[#ef4444]/5':
                                        earn < 0,
                                }
                            )}
                        >
                            <div
                                className={clsx(
                                    'absolute inset-0 bg-gradient-to-br from-transparent to-danger/5 pointer-events-none',
                                    {
                                        'to-[#10b981]/5': earn >= 0,
                                        'to-[#ef4444]/5': earn < 0,
                                    }
                                )}
                            />
                            <span
                                className={clsx(
                                    'text-[10px] font-bold uppercase mb-1 tracking-wider opacity-90',
                                    {
                                        'text-[#ef4444]': earn < 0,
                                        'text-[#10b981]': earn >= 0,
                                    }
                                )}
                            >
                                Total PNL
                            </span>
                            <div className="flex items-baseline gap-1 relative z-10">
                                <span
                                    className={clsx(
                                        ' text-3xl font-bold tracking-tight',
                                        {
                                            'text-[#ef4444]': earn < 0,
                                            'text-[#10b981]': earn >= 0,
                                        }
                                    )}
                                >
                                    {earn > 0 ? '+' : ''}{' '}
                                    {formatDisplay(earn, {
                                        decimalToShow: 2,
                                    })}{' '}
                                </span>
                                <span
                                    className={clsx(' text-xs font-semibold', {
                                        'text-[#ef4444]': earn < 0,
                                        'text-[#10b981]': earn >= 0,
                                    })}
                                >
                                    {item.tradeCoinName}
                                </span>
                            </div>
                            <span
                                className={clsx(
                                    ' text-xs font-medium mt-1.5 bg-white/60  px-2 py-0.5 rounded-md',
                                    {
                                        'text-[#ef4444]/90': earn < 0,
                                        'text-[#10b981]/90': earn >= 0,
                                    }
                                )}
                            >
                                {percentage(pnl)}
                            </span>
                        </div>

                        <div className="h-2" />
                    </div>
                </Sheet.Content>
            </Sheet.Container>
            <Sheet.Backdrop className="" onTap={onClose} />
        </Sheet>
    );
}
